import React from 'react';
import { MapPin, Clock, Route as RouteIcon, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import NotFound from './NotFound';
import { useApp } from '../context/AppContext';
import { findRoute, allRoutes, coordsFor } from '../mock/content';
import { buildSections } from '../mock/blogContent';
import { currencySymbols, rates } from '../mock/mock';
import useSEO from '../hooks/useSEO';
import RouteMap from '../components/RouteMap';
import WeatherBadge from '../components/WeatherBadge';

const CityPage = () => {
  const { slug } = useParams();
  const { lang, currency, priceFor, vehicles } = useApp();
  const route = findRoute(slug);
  const fmt = (n) => `${currencySymbols[currency]} ${Math.round(n * rates[currency])}`;
  const minFactor = vehicles.length ? Math.min(...vehicles.map((v) => Number(v.priceFactor) || 1)) : 1;
  const dynPrice = route ? (priceFor(route.km, minFactor) || route.price) : 0;

  const airportName = route ? route.airport.name : '';
  const cityName = route ? route.city.name : '';
  const origin = route ? (route.fromType === 'airport' ? airportName : cityName) : '';
  const dest = route ? (route.fromType === 'airport' ? cityName : airportName) : '';

  const title = route ? {
    de: `Taxi ${origin} nach ${dest} | Flughafentransfer Festpreis`,
    en: `Taxi ${origin} to ${dest} | Airport Transfer Fixed Price`,
    fr: `Taxi ${origin} à ${dest} | Transfert aéroport prix fixe`,
    it: `Taxi ${origin} a ${dest} | Transfer aeroporto prezzo fisso`,
  }[lang] : 'Transfer | CAB.TAXI Zürich';
  const desc = route ? {
    de: `Zuverlässiges Taxi und Flughafentransfer von ${origin} nach ${dest}. Festpreis ab ${fmt(dynPrice)}, kostenlose Stornierung, professionelle Chauffeure.`,
    en: `Reliable taxi and airport transfer from ${origin} to ${dest}. Fixed price from ${fmt(dynPrice)}, free cancellation, professional chauffeurs.`,
    fr: `Taxi et transfert aéroport fiable de ${origin} à ${dest}. Prix fixe dès ${fmt(dynPrice)}, annulation gratuite.`,
    it: `Taxi e transfer aeroporto affidabile da ${origin} a ${dest}. Prezzo fisso da ${fmt(dynPrice)}, cancellazione gratuita.`,
  }[lang] : '';
  useSEO(title, desc, { skip: !route });

  if (!route) {
    return <NotFound />;
  }

  const L = {
    de: { h: `Taxi & Transfer: ${origin} → ${dest}`, from: 'Festpreis ab', book: 'Jetzt buchen', dist: 'Distanz', dur: 'Fahrzeit', why: 'Warum dieser Transfer?', others: 'Weitere beliebte Strecken', incl: ['Festpreis ohne versteckte Gebühren', 'Flug-Tracking & kostenlose Wartezeit', 'Professioneller Chauffeur mit Namensschild', 'Kostenlose Stornierung bis 24 Std.'] },
    en: { h: `Taxi & Transfer: ${origin} → ${dest}`, from: 'Fixed price from', book: 'Book now', dist: 'Distance', dur: 'Duration', why: 'Why this transfer?', others: 'Other popular routes', incl: ['Fixed price with no hidden fees', 'Flight tracking & free waiting time', 'Professional chauffeur with name sign', 'Free cancellation up to 24h'] },
    fr: { h: `Taxi & transfert : ${origin} → ${dest}`, from: 'Prix fixe dès', book: 'Réserver', dist: 'Distance', dur: 'Durée', why: 'Pourquoi ce transfert ?', others: 'Autres trajets populaires', incl: ['Prix fixe sans frais cachés', 'Suivi de vol & attente gratuite', 'Chauffeur professionnel avec pancarte', 'Annulation gratuite jusqu’à 24h'] },
    it: { h: `Taxi & transfer: ${origin} → ${dest}`, from: 'Prezzo fisso da', book: 'Prenota', dist: 'Distanza', dur: 'Durata', why: 'Perché questo transfer?', others: 'Altre tratte popolari', incl: ['Prezzo fisso senza costi nascosti', 'Monitoraggio volo & attesa gratuita', 'Autista professionista con cartello', 'Cancellazione gratuita fino a 24h'] },
  }[lang];

  const related = allRoutes().filter((r) => r.airport.code === route.airport.code && r.slug !== route.slug).slice(0, 8);
  const rich = buildSections(`${origin} → ${dest}`, 'route', lang);
  const priceTitle = { de: 'Preise pro Fahrzeug', en: 'Prices per vehicle', fr: 'Prix par véhicule', it: 'Prezzi per veicolo' }[lang];
  const sortedVehicles = [...vehicles].sort((a, b) => (Number(a.priceFactor) || 1) - (Number(b.priceFactor) || 1));

  return (
    <Layout>
      <section className="relative brand-gradient">
        <div className="absolute inset-0">
          <img src={route.city.img} alt={cityName} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(12,44,72,.92), rgba(18,62,99,.8))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-center gap-2 text-white/70 text-sm"><MapPin className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> Switzerland</div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white max-w-2xl leading-tight">{L.h}</h1>
          <div className="mt-6 flex flex-wrap gap-8">
            <div><div className="text-xs text-white/60">{L.dist}</div><div className="text-2xl font-extrabold text-white flex items-center gap-2"><RouteIcon className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} /> {route.km} km</div></div>
            <div><div className="text-xs text-white/60">{L.dur}</div><div className="text-2xl font-extrabold text-white flex items-center gap-2"><Clock className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} /> ~{route.mins} min</div></div>
            <div><div className="text-xs text-white/60">{L.from}</div><div className="text-2xl font-extrabold" style={{ color: 'var(--brand-accent)' }}>{fmt(dynPrice)}</div></div>
          </div>
          <a href="/#book" className="accent-btn mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold">{L.book} <ArrowRight className="w-4 h-4" /></a>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-extrabold text-[color:var(--brand-dark)] flex items-center gap-2"><ShieldCheck className="w-6 h-6" style={{ color: 'var(--brand-blue)' }} /> {L.why}</h2>
            <ul className="mt-5 space-y-3">
              {L.incl.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-600"><CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--brand-accent)' }} /> {it}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <img src={route.city.img} alt={cityName} className="rounded-2xl w-full h-72 object-cover shadow-lg" />
            <RouteMap from={route.fromType === 'airport' ? `${airportName}, Switzerland` : `${cityName}, Switzerland`} to={route.fromType === 'airport' ? `${cityName}, Switzerland` : `${airportName}, Switzerland`} />
            <WeatherBadge coords={coordsFor(cityName)} city={cityName} lang={lang} />
          </div>
        </div>
      </section>

      {/* Dynamic per-vehicle prices (driven by admin pricing config) */}
      <section className="pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[color:var(--brand-dark)] mb-6">{priceTitle} · {route.km} km</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedVehicles.map((v) => (
              <div key={v.id} data-testid={`city-price-${v.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-[color:var(--brand-dark)]">{v.name}</div>
                  <div className="text-xs text-gray-400">{v.pax} pax · {v.bags} bags</div>
                </div>
                <div className="text-xl font-extrabold shrink-0" style={{ color: 'var(--brand-accent)' }}>{fmt(priceFor(route.km, v))}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form article */}
      <section className="pb-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="space-y-9">
            {rich.sections.map((s, i) => (
              <div key={i} data-testid={`city-section-${i}`}>
                <h2 className="text-xl sm:text-2xl font-extrabold text-[color:var(--brand-dark)] mb-3">{s.h}</h2>
                <div className="space-y-4 text-[15px] leading-relaxed text-gray-700">
                  {s.paras.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10" data-testid="city-faq">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[color:var(--brand-dark)] mb-4">{rich.labels.faq}</h2>
            <div className="space-y-3">
              {rich.faqs.map((f) => (
                <div key={f.q} className="rounded-xl border border-gray-100 bg-white p-4">
                  <div className="font-bold text-[color:var(--brand-dark)]">{f.q}</div>
                  <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-[color:var(--brand-muted)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[color:var(--brand-dark)] mb-6">{L.others}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((r) => {
              const o = r.fromType === 'airport' ? r.airport.name : r.city.name;
              const d = r.fromType === 'airport' ? r.city.name : r.airport.name;
              return (
                <Link to={`/taxi/${r.slug}`} key={r.slug} className="card-hover bg-white rounded-xl p-4 flex items-center gap-2 text-sm font-semibold text-[color:var(--brand-dark)]">
                  <MapPin className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent-hover)' }} /> {o} → {d}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CityPage;
